import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const catMeta: Record<string, { title: string; description: string; keywords: string }> = {
  works: {
    title: '作品集 — 安斋拉拉',
    description: '安斋拉拉历年作品全收录，按发布时间排序，涵盖所有系列与特别企划。收录200+部作品信息，支持分类检索与高清预览。',
    keywords: '安斋拉拉作品,安斋拉拉合集,安斋拉拉经典作品,安斋拉拉作品列表',
  },
  profile: {
    title: '资料档案 — 安斋拉拉',
    description: '完整的安斋拉拉个人资料库，包括基本档案、职业生涯时间线、获奖荣誉等详尽信息，全方位了解她的成长轨迹。',
    keywords: '安斋拉拉资料,安斋拉拉档案,安斋拉拉个人资料,安斋拉拉简历',
  },
  news: {
    title: '最新动态 — 安斋拉拉',
    description: '实时追踪安斋拉拉最新发布、活动动态与媒体报道，第一时间掌握新鲜资讯。关注安斋拉拉最新作品、采访与社交动态。',
    keywords: '安斋拉拉最新动态,安斋拉拉新闻,安斋拉拉最新消息,安斋拉拉资讯',
  },
  community: {
    title: '粉丝社区 — 安斋拉拉',
    description: '加入安斋拉拉粉丝大家庭，参与讨论、分享收藏、交流心得。活跃氛围、丰富活动，与同好共聚温馨社区。',
    keywords: '安斋拉拉粉丝,安斋拉拉社区,安斋拉拉粉丝群,安斋拉拉论坛',
  },
  gallery: {
    title: '精选图集 — 安斋拉拉',
    description: '精选安斋拉拉高清写真与活动照片，定期更新，带来视觉盛宴。收录多套精美写真图集。',
    keywords: '安斋拉拉图集,安斋拉拉写真,安斋拉拉照片,安斋拉拉美图',
  },
  interviews: {
    title: '深度访谈 — 安斋拉拉',
    description: '收录安斋拉拉各类专访与深度对谈，了解银幕背后最真实的她。独家访谈、幕后故事、职业感悟。',
    keywords: '安斋拉拉访谈,安斋拉拉专访,安斋拉拉采访,安斋拉拉对话',
  },
};

const categoryImages: Record<string, string> = {
  works: 'https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20header%20with%20warm%20coral%20and%20cream%20gradient%20tones%2C%20soft%20atmospheric%20lighting%2C%20abstract%20geometric%20composition%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=400&seq=cat-header-works&orientation=landscape',
  profile: 'https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20header%20with%20warm%20coral%20and%20cream%20gradient%20tones%2C%20soft%20atmospheric%20lighting%2C%20abstract%20geometric%20composition%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=400&seq=cat-header-profile&orientation=landscape',
  news: 'https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20header%20with%20warm%20coral%20and%20cream%20gradient%20tones%2C%20soft%20atmospheric%20lighting%2C%20abstract%20geometric%20composition%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=400&seq=cat-header-news&orientation=landscape',
  community: 'https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20header%20with%20warm%20coral%20and%20cream%20gradient%20tones%2C%20soft%20atmospheric%20lighting%2C%20abstract%20geometric%20composition%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=400&seq=cat-header-community&orientation=landscape',
  gallery: 'https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20header%20with%20warm%20coral%20and%20cream%20gradient%20tones%2C%20soft%20atmospheric%20lighting%2C%20abstract%20geometric%20composition%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=400&seq=cat-header-gallery&orientation=landscape',
  interviews: 'https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20header%20with%20warm%20coral%20and%20cream%20gradient%20tones%2C%20soft%20atmospheric%20lighting%2C%20abstract%20geometric%20composition%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=400&seq=cat-header-interviews&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const meta = slug ? catMeta[slug] : null;

  const filteredArticles = useMemo(() => {
    if (!slug) return [];
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  const tabs = categories.map((c) => c.slug);

  if (!category || !meta) {
    return (
      <div className="pt-20 pb-10 text-center">
        <p className="text-foreground-500">分类不存在</p>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">返回首页</Link>
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: meta.title,
    description: meta.description,
    url: `https://anzai-rara.com/category/${slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://anzai-rara.com/category/${slug}`}
      />

      <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
        <img
          src={categoryImages[slug] || categoryImages.works}
          alt={category.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-50/20 flex items-center justify-center">
              <i className={`${category.icon} text-2xl text-background-50`}></i>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
              {category.name}
            </h1>
            <p className="text-background-50/70 text-sm md:text-base max-w-lg mx-auto">
              {category.description}
            </p>
          </div>
        </div>
      </div>

      <Breadcrumb
        items={[
          { name: '首页', path: '/' },
          { name: category.name },
        ]}
      />

      <div className="w-full bg-background-50 border-b border-background-200 sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {tabs.map((tab) => {
              const cat = categories.find((c) => c.slug === tab);
              const isActive = tab === slug;
              return (
                <Link
                  key={tab}
                  to={`/category/${tab}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer
                    ${isActive
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                >
                  {cat?.name || tab}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-3">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无内容</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}